import { useCallback, useMemo, useState } from "react";
import type {
  AccessLevel,
  Artwork,
  ArtworkPartWrite,
  ArtworkWrite,
  RightsLicense,
  YearPrecision,
} from "../../api/types";
import type { PartDraft } from "./PartsEditor";
import { ValidationError } from "../../api/errors";
import { tr } from "../../i18n/strings";
import { MEDIA_RIGHTS_ENABLED } from "./flags";

export interface FormState {
  title: string;
  inventory_code: string;
  artist: string | null;
  artistName: string;
  type: string | null;
  subtype: string | null;
  year_precision: YearPrecision;
  year_start: number | null;
  year_end: number | null;
  artwork_width_cm: number | null;
  artwork_height_cm: number | null;
  artwork_depth_cm: number | null;
  frame_width_cm: number | null;
  frame_height_cm: number | null;
  frame_depth_cm: number | null;
  is_life_size: boolean;
  edition_number: number | null;
  edition_size: number | null;
  parts: PartDraft[];
  mediums: string[];
  style: string | null;
  curatorial_notes: string;
  ownership_status: string | null;
  price_asking: number | null;
  price_sold: number | null;
  price_estimate: number | null;
  currency: string | null;
  condition_status: string | null;
  condition_notes: string;
  provenance_text: string;
  provenance_confidence: string | null;
  authenticity_notes: string;

  /**
   * The `media_*` trio belongs to the primary attachment, not to the artwork row.
   * They live here so the one dirty check and the one validation pass cover them;
   * `toPayload` deliberately leaves them out.
   */
  media_rights: RightsLicense | "";
  media_rights_note: string;
  media_access_level: AccessLevel;
}

export const blankForm: FormState = {
  title: "",
  inventory_code: "",
  artist: null,
  artistName: "",
  type: null,
  subtype: null,
  year_precision: "unknown",
  year_start: null,
  year_end: null,
  artwork_width_cm: null,
  artwork_height_cm: null,
  artwork_depth_cm: null,
  frame_width_cm: null,
  frame_height_cm: null,
  frame_depth_cm: null,
  is_life_size: false,
  edition_number: null,
  edition_size: null,
  parts: [],
  mediums: [],
  style: null,
  curatorial_notes: "",
  ownership_status: null,
  price_asking: null,
  price_sold: null,
  price_estimate: null,
  currency: null,
  condition_status: null,
  condition_notes: "",
  provenance_text: "",
  provenance_confidence: null,
  authenticity_notes: "",
  media_rights: "",
  media_rights_note: "",
  media_access_level: "private",
};

export function formFromArtwork(artwork: Artwork): FormState {
  const primary = artwork.attachments.find((file) => file.kind === "primary");
  return {
    title: artwork.title,
    inventory_code: artwork.inventory_code,
    artist: artwork.artist,
    artistName: artwork.artist_detail?.name ?? "",
    type: artwork.type,
    subtype: artwork.subtype,
    year_precision: artwork.year_precision,
    year_start: artwork.year_start,
    year_end: artwork.year_end,
    artwork_width_cm: artwork.artwork_width_cm,
    artwork_height_cm: artwork.artwork_height_cm,
    artwork_depth_cm: artwork.artwork_depth_cm,
    frame_width_cm: artwork.frame_width_cm,
    frame_height_cm: artwork.frame_height_cm,
    frame_depth_cm: artwork.frame_depth_cm,
    is_life_size: artwork.is_life_size ?? false,
    edition_number: artwork.edition_number ?? null,
    edition_size: artwork.edition_size ?? null,
    parts: (artwork.parts ?? []).map((part) => ({
      key: part.id,
      label: part.label,
      width_cm: part.width_cm,
      height_cm: part.height_cm,
      depth_cm: part.depth_cm,
    })),
    mediums: artwork.mediums,
    style: artwork.style,
    curatorial_notes: artwork.curatorial_notes,
    ownership_status: artwork.ownership_status,
    price_asking: artwork.price_asking,
    price_sold: artwork.price_sold,
    price_estimate: artwork.price_estimate,
    currency: artwork.currency,
    condition_status: artwork.condition_status,
    condition_notes: artwork.condition_notes,
    provenance_text: artwork.provenance_text,
    provenance_confidence: artwork.provenance_confidence,
    authenticity_notes: artwork.authenticity_notes,
    media_rights: primary?.rights ?? "",
    media_rights_note: primary?.rights_note ?? "",
    media_access_level: primary?.access_level ?? "private",
  };
}

const DIMENSIONS = [
  "artwork_width_cm",
  "artwork_height_cm",
  "artwork_depth_cm",
  "frame_width_cm",
  "frame_height_cm",
  "frame_depth_cm",
] as const;

export type FormErrors = Partial<Record<keyof FormState | "detail", string>> &
  Record<string, string | undefined>;

/**
 * Mirrors the server's validators so the user sees the problem before the
 * request. The server still enforces all of it — `applyServerErrors` puts a
 * 400 back onto the same fields.
 */
export function validateForm(state: FormState): FormErrors {
  // Not a hook: this runs on submit, so reading the language now is correct.
  const t = tr();
  const errors: FormErrors = {};

  if (!state.title.trim()) errors.title = t.form.requiredTitle;
  if (!state.type) errors.type = t.form.requiredType;

  if (state.year_precision === "unknown") {
    // Nothing to check: the payload drops both bounds.
  } else if (state.year_start === null) {
    errors.year_start = t.form.yearStartRequired;
  } else if (state.year_precision === "range") {
    if (state.year_end === null) errors.year_end = t.form.yearEndRequired;
    else if (state.year_end < state.year_start) errors.year_end = t.form.yearOrder;
  }

  const priced = [state.price_asking, state.price_sold, state.price_estimate].some(
    (value) => value !== null,
  );
  if (priced && !state.currency) errors.currency = t.form.currencyRequired;

  // Guarded: a stored "custom" without a note would otherwise block every save
  // on an error attached to a field that is no longer rendered.
  if (MEDIA_RIGHTS_ENABLED && state.media_rights === "custom" && !state.media_rights_note.trim()) {
    errors.media_rights_note = t.form.rightsNoteRequired;
  }

  for (const key of DIMENSIONS) {
    const value = state[key];
    if (value === null) continue;
    if (!Number.isFinite(value) || value <= 0) {
      errors[key] = t.form.dimensionPositive;
    }
  }

  const editionNumber = state.edition_number;
  const editionSize = state.edition_size;
  if (editionNumber !== null || editionSize !== null) {
    if (editionNumber === null || editionSize === null) {
      errors.edition_number = t.form.editionBoth;
      errors.edition_size = t.form.editionBoth;
    } else if (editionNumber < 1 || editionSize < 1) {
      errors.edition_number = t.form.editionMin;
    } else if (editionNumber > editionSize) {
      errors.edition_number = t.form.editionOrder;
    }
  }

  state.parts.forEach((part, index) => {
    for (const field of ["width_cm", "height_cm", "depth_cm"] as const) {
      const value = part[field];
      if (value === null) continue;
      if (!Number.isFinite(value) || value <= 0) {
        errors[`parts.${index}.${field}`] = t.form.dimensionPositive;
      }
    }
  });

  return errors;
}

/**
 * Exact and circa are a single year; the server stores it as a degenerate range.
 *
 * The key list is exhaustive on purpose: the `media_*` fields describe an
 * attachment and would be rejected by the artwork endpoint.
 */

function finiteOrNull(value: number | null): number | null {
  return value !== null && Number.isFinite(value) ? value : null;
}

export function toPayload(state: FormState): ArtworkWrite {
  const unknown = state.year_precision === "unknown";
  const range = state.year_precision === "range";

  return {
    title: state.title.trim(),
    inventory_code: state.inventory_code.trim(),
    artist: state.artist,
    type: state.type!,
    subtype: state.subtype,
    year_precision: state.year_precision,
    year_start: unknown ? null : state.year_start,
    year_end: unknown ? null : range ? state.year_end : state.year_start,
    artwork_width_cm: finiteOrNull(state.artwork_width_cm),
    artwork_height_cm: finiteOrNull(state.artwork_height_cm),
    artwork_depth_cm: finiteOrNull(state.artwork_depth_cm),
    frame_width_cm: finiteOrNull(state.frame_width_cm),
    frame_height_cm: finiteOrNull(state.frame_height_cm),
    frame_depth_cm: finiteOrNull(state.frame_depth_cm),
    is_life_size: state.is_life_size,
    edition_number: state.edition_number,
    edition_size: state.edition_size,
    parts: state.parts.map(
      (part): ArtworkPartWrite => ({
        label: part.label.trim(),
        width_cm: finiteOrNull(part.width_cm),
        height_cm: finiteOrNull(part.height_cm),
        depth_cm: finiteOrNull(part.depth_cm),
      }),
    ),
    mediums: state.mediums,
    style: state.style,
    curatorial_notes: state.curatorial_notes,
    ownership_status: state.ownership_status,
    price_asking: state.price_asking,
    price_sold: state.price_sold,
    price_estimate: state.price_estimate,
    currency: state.currency,
    condition_status: state.condition_status,
    condition_notes: state.condition_notes,
    provenance_text: state.provenance_text,
    provenance_confidence: state.provenance_confidence,
    authenticity_notes: state.authenticity_notes,
  };
}

export function useArtworkForm(initial: FormState) {
  const [state, setState] = useState<FormState>(initial);
  const [errors, setErrors] = useState<FormErrors>({});
  const [baseline, setBaseline] = useState<FormState>(initial);

  const set = useCallback(<K extends keyof FormState>(key: K, value: FormState[K]) => {
    setState((current) => ({ ...current, [key]: value }));
    setErrors((current) => (current[key] ? { ...current, [key]: undefined } : current));
  }, []);

  const patch = useCallback((changes: Partial<FormState>) => {
    setState((current) => ({ ...current, ...changes }));
  }, []);

  const reset = useCallback((next: FormState) => {
    setState(next);
    setBaseline(next);
    setErrors({});
  }, []);

  const dirty = useMemo(
    () => JSON.stringify(state) !== JSON.stringify(baseline),
    [state, baseline],
  );

  const check = useCallback(() => {
    const found = validateForm(state);
    setErrors(found);
    return Object.keys(found).length === 0;
  }, [state]);

  /** Maps a DRF 400 back onto the inputs that produced it. */
  const applyServerErrors = useCallback((error: unknown) => {
    if (!(error instanceof ValidationError)) return;
    const mapped: FormErrors = {};
    for (const [field, messages] of Object.entries(error.fields)) {
      mapped[field as keyof FormState] = messages[0];
    }
    setErrors(mapped);
  }, []);

  return { state, set, patch, reset, errors, setErrors, dirty, check, applyServerErrors };
}
